/**
 * Global Parameters Configuration Component
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { Parameter, ParameterType } from '../../types/parameters';

const GlobalParameters: React.FC = () => {
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    default_coverage_days: 14,
    safety_stock_multiplier: 1.5,
    service_level_target: 98,
    forecast_horizon_months: 12,
    min_order_frequency_days: 7,
    max_order_frequency_days: 30,
  });

  // Last successfully-saved snapshot. Used to "freeze" the Save button
  // when there are no pending changes (unsaved edits).
  const [savedData, setSavedData] = useState<typeof formData | null>(null);

  // Fetch global parameters
  const { data: parameters, isLoading } = useQuery<Parameter[]>({
    queryKey: ['parameters', 'global'],
    queryFn: async () => {
      const response = await api.getParameters({ param_type: ParameterType.GLOBAL });
      return response.data;
    },
  });

  // Populate form when parameters are loaded
  useEffect(() => {
    if (parameters) {
      const paramMap: Record<string, any> = {};
      parameters.forEach((param: Parameter) => {
        if (param.param_value && typeof param.param_value === 'object') {
          // Extract the actual value from the param_value object
          const value = Object.values(param.param_value)[0];
          paramMap[param.param_name] = value;
        }
      });
      setFormData((prev) => {
        const next = { ...prev, ...paramMap };
        setSavedData(next);
        return next;
      });
    }
  }, [parameters]);

  // Are there unsaved changes? Compare the editable state against the last
  // successful save. If equal, the Save button is frozen (disabled).
  const hasUnsavedChanges = React.useMemo(() => {
    if (!savedData) return true; // never loaded from backend — allow first save
    return JSON.stringify(formData) !== JSON.stringify(savedData);
  }, [formData, savedData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const updates = [];

      for (const [paramName, value] of Object.entries(data)) {
        const paramValue: Record<string, any> = {};

        // Structure the param_value based on parameter type
        switch (paramName) {
          case 'default_coverage_days':
            paramValue.coverage_days = value;
            break;
          case 'safety_stock_multiplier':
            paramValue.multiplier = value;
            break;
          case 'service_level_target':
            paramValue.target_percentage = value;
            break;
          case 'forecast_horizon_months':
            paramValue.months = value;
            break;
          case 'min_order_frequency_days':
          case 'max_order_frequency_days':
            paramValue.days = value;
            break;
          default:
            paramValue.value = value;
        }

        updates.push({
          param_type: ParameterType.GLOBAL,
          param_name: paramName,
          param_value: paramValue,
          description: getParameterDescription(paramName),
        });
      }

      return await api.bulkUpdateParameters({
        parameters: updates,
        change_reason: 'Global parameters update',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parameters'] });
      setSuccessMessage('Global parameters saved successfully!');
      setErrorMessage('');
      // Rebase the clean snapshot so the Save button freezes until the
      // user makes a new edit.
      setSavedData(formData);
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (error: any) => {
      setErrorMessage(`Error saving parameters: ${error.response?.data?.detail || error.message}`);
      setSuccessMessage('');
    },
  });

  const getParameterDescription = (paramName: string): string => {
    const descriptions: Record<string, string> = {
      default_coverage_days: 'Default number of days of inventory coverage to maintain',
      safety_stock_multiplier: 'Multiplier for safety stock calculation based on demand variability',
      service_level_target: 'Target service level percentage (fill rate)',
      forecast_horizon_months: 'Number of months to forecast ahead',
      min_order_frequency_days: 'Minimum days between replenishment orders',
      max_order_frequency_days: 'Maximum days between replenishment orders',
    };
    return descriptions[paramName] || '';
  };

  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  const handleReset = () => {
    if (parameters) {
      const paramMap: Record<string, any> = {};
      parameters.forEach((param: Parameter) => {
        if (param.param_value && typeof param.param_value === 'object') {
          const value = Object.values(param.param_value)[0];
          paramMap[param.param_name] = value;
        }
      });
      setFormData((prev) => ({ ...prev, ...paramMap }));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading parameters...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Introduction */}
      <Typography variant="h6" gutterBottom>
        System-Wide Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        These parameters apply across all stores and products as default values
      </Typography>

      <Grid container spacing={3}>
        {/* Inventory Coverage */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Inventory Coverage
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Default Coverage Days"
                    value={formData.default_coverage_days}
                    onChange={(e) =>
                      setFormData({ ...formData, default_coverage_days: parseFloat(e.target.value) })
                    }
                    helperText="Target days of inventory to maintain (e.g., 14 = 2 weeks)"
                    inputProps={{ min: 1, max: 365, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Safety Stock Multiplier"
                    value={formData.safety_stock_multiplier}
                    onChange={(e) =>
                      setFormData({ ...formData, safety_stock_multiplier: parseFloat(e.target.value) })
                    }
                    helperText="Buffer multiplier for demand variability (e.g., 1.5 = 50% buffer)"
                    inputProps={{ min: 1.0, max: 5.0, step: 0.1 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Service Level */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Service Level
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Service Level Target (%)"
                    value={formData.service_level_target}
                    onChange={(e) =>
                      setFormData({ ...formData, service_level_target: parseFloat(e.target.value) })
                    }
                    helperText="Target fill rate percentage (e.g., 98 = 98% of orders filled)"
                    inputProps={{ min: 80, max: 100, step: 0.5 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Forecasting */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Forecasting
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Forecast Horizon (Months)"
                    value={formData.forecast_horizon_months}
                    onChange={(e) =>
                      setFormData({ ...formData, forecast_horizon_months: parseInt(e.target.value) })
                    }
                    helperText="Number of months to forecast ahead (e.g., 12 = 1 year)"
                    inputProps={{ min: 1, max: 24, step: 1 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Frequency */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Frequency
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Minimum Order Frequency (Days)"
                    value={formData.min_order_frequency_days}
                    onChange={(e) =>
                      setFormData({ ...formData, min_order_frequency_days: parseInt(e.target.value) })
                    }
                    helperText="Minimum days between orders (e.g., 7 = weekly)"
                    inputProps={{ min: 1, max: 365, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Maximum Order Frequency (Days)"
                    value={formData.max_order_frequency_days}
                    onChange={(e) =>
                      setFormData({ ...formData, max_order_frequency_days: parseInt(e.target.value) })
                    }
                    helperText="Maximum days between orders (e.g., 30 = monthly)"
                    inputProps={{ min: 1, max: 365, step: 1 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset}>
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saveMutation.isPending || !hasUnsavedChanges}
            >
              {hasUnsavedChanges ? 'Save Changes' : 'Saved'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GlobalParameters;
